import React, { useEffect, useState } from "react";
import { ongcCentreApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./OngcCentre.scss";

const defaultData = {
  _id: null,
  PageTitle: "",
  PageSubtitle: "",
  Description1: "",
  Description2: "",
  YoutubeLink: "",
  Objectives: [],
  ActivitiesDescription: "",
  Activities: [],
  CentresSectionTitle: "Established Institutes & Centres:",
  Centres: [],
};

const OngcCentre = () => {
  const [formData, setFormData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [objectivesInput, setObjectivesInput] = useState("");
  const [activitiesInput, setActivitiesInput] = useState("");
  const [centresList, setCentresList] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient(ongcCentreApi);
      const result = await response.json();
      if (response.ok && result.data) {
        const fetchedData = Array.isArray(result.data) ? result.data[0] : result.data;
        if (fetchedData) {
          setFormData(fetchedData);
          if (fetchedData.Objectives) {
            const objs = fetchedData.Objectives.map(o => `${o.title}: ${o.desc}`).join("\n");
            setObjectivesInput(objs);
          }
          if (fetchedData.Activities) {
            setActivitiesInput(fetchedData.Activities.join("\n"));
          }
          if (fetchedData.Centres && Array.isArray(fetchedData.Centres)) {
            setCentresList(fetchedData.Centres.map(c => ({
              title: c.title || "",
              pointsInput: Array.isArray(c.points) ? c.points.join("\n") : (c.points || "")
            })));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching ONGC Centre data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleObjectivesChange = (e) => {
    setObjectivesInput(e.target.value);
  };

  const handleActivitiesChange = (e) => {
    setActivitiesInput(e.target.value);
  };

  const handleCentreTitleChange = (index, value) => {
    setCentresList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], title: value };
      return updated;
    });
  };

  const handleCentrePointsChange = (index, value) => {
    setCentresList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], pointsInput: value };
      return updated;
    });
  };

  const addCentreItem = () => {
    setCentresList(prev => [...prev, { title: "", pointsInput: "" }]);
  };

  const removeCentreItem = (index) => {
    setCentresList(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Parse objectives (format: "Title: Description")
      const parsedObjectives = objectivesInput.split("\n").filter(line => line.trim() !== "").map(line => {
        const parts = line.split(":");
        if (parts.length >= 2) {
          const title = parts[0].trim();
          const desc = parts.slice(1).join(":").trim();
          return { title, desc };
        }
        return { title: "Objective", desc: line.trim() };
      });

      // Parse activities
      const parsedActivities = activitiesInput.split("\n").map(line => line.trim()).filter(line => line !== "");

      // Parse centres
      const parsedCentres = centresList.map(c => ({
        title: c.title.trim(),
        points: (c.pointsInput || "").split("\n").map(p => p.trim()).filter(p => p !== "")
      })).filter(c => c.title || c.points.length > 0);

      const payload = {
        ...formData,
        Objectives: parsedObjectives,
        Activities: parsedActivities,
        Centres: parsedCentres,
      };

      const method = payload._id ? "PUT" : "POST";
      const url = payload._id ? `${ongcCentreApi}/${payload._id}` : ongcCentreApi;

      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        fetchData();
      } else {
        console.error('[Frontend] Save failed with status:', response.status);
      }
    } catch (error) {
      console.error("Error saving ONGC Centre data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ongc-admin-page">
      <div className="ongc-admin-page__header">
        <div>
          <h1>{formData.PageTitle || "ONGC Centre of Advanced Studies"}</h1>
          <p>{formData.PageSubtitle || "Manage the description, objectives, and activities of the ONGC Centre."}</p>
        </div>
        <button className="save-btn" type="submit" form="ongcForm" disabled={loading}>{loading ? "Saving..." : saved ? "✓ Saved" : "Save Changes"}</button>
      </div>

      <form id="ongcForm" className="ongc-form" onSubmit={handleSubmit}>
        <div className="ongc-card">
          <div className="form-section-label">General Information</div>
          <div className="form-group">
            <label>Page Title</label>
            <input type="text" name="PageTitle" value={formData.PageTitle || ""} onChange={handleChange} placeholder="e.g. ONGC Centre of Advanced Studies" />
          </div>
          <div className="form-group">
            <label>Page Subtitle / Sub-heading</label>
            <input type="text" name="PageSubtitle" value={formData.PageSubtitle || ""} onChange={handleChange} placeholder="e.g. Manage the description, objectives, and activities of the ONGC Centre." />
          </div>
          <div className="form-group">
            <label>Paragraph 1</label>
            <textarea name="Description1" rows="5" value={formData.Description1 || ""} onChange={handleChange} placeholder="First paragraph description..."></textarea>
          </div>
          <div className="form-group">
            <label>Paragraph 2</label>
            <textarea name="Description2" rows="5" value={formData.Description2 || ""} onChange={handleChange} placeholder="Second paragraph description..."></textarea>
          </div>
          <div className="form-group">
            <label>YouTube Link</label>
            <input type="text" name="YoutubeLink" value={formData.YoutubeLink || ""} onChange={handleChange} placeholder="e.g. https://www.youtube.com/..." />
          </div>

          <div className="form-section-separator"></div>

          <div className="ongc-grid">
            <div className="ongc-section">
              <div className="form-section-label">Objectives</div>
              <div className="form-group">
                <label>Objectives List (Format: "Title: Description", one per line)</label>
                <textarea 
                  rows="10" 
                  value={objectivesInput} 
                  onChange={handleObjectivesChange} 
                  placeholder="Advance Research: To conduct advance research in identified areas." 
                />
              </div>
            </div>

            <div className="ongc-section">
              <div className="form-section-label">Activities</div>
              <div className="form-group">
                <label>Activities Description</label>
                <textarea name="ActivitiesDescription" rows="3" value={formData.ActivitiesDescription || ""} onChange={handleChange} placeholder="Introductory paragraph for activities..."></textarea>
              </div>
              <div className="form-group">
                <label>Activities List (One per line)</label>
                <textarea 
                  rows="10" 
                  value={activitiesInput} 
                  onChange={handleActivitiesChange} 
                  placeholder="Developing trained human resource..." 
                />
              </div>
            </div>
          </div>

          <div className="form-section-separator"></div>

          <div className="form-section-label">Centres / Institutes Section</div>
          <div className="form-group">
            <label>Section Title</label>
            <input 
              type="text" 
              name="CentresSectionTitle" 
              value={formData.CentresSectionTitle || ""} 
              onChange={handleChange} 
              placeholder="e.g. Established Institutes & Centres:" 
            />
          </div>

          <div className="centres-admin-list">
            {centresList.map((centre, idx) => (
              <div key={idx} className="centre-admin-card">
                <div className="centre-admin-card__header">
                  <span>Centre / Institute #{idx + 1}</span>
                  <button type="button" className="remove-btn" onClick={() => removeCentreItem(idx)}>✕ Remove</button>
                </div>
                <div className="form-group">
                  <label>Centre Name / Title</label>
                  <input 
                    type="text" 
                    value={centre.title} 
                    onChange={(e) => handleCentreTitleChange(idx, e.target.value)} 
                    placeholder="e.g. Institute of Hydrocarbon, Energy & Geo-Resources" 
                  />
                </div>
                <div className="form-group">
                  <label>Bullet Points (One per line)</label>
                  <textarea 
                    rows="5" 
                    value={centre.pointsInput} 
                    onChange={(e) => handleCentrePointsChange(idx, e.target.value)} 
                    placeholder="First bullet point...&#10;Second bullet point..." 
                  />
                </div>
              </div>
            ))}

            <button type="button" className="add-centre-btn" onClick={addCentreItem}>
              + Add Centre / Institute
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OngcCentre;
